/**
 * The three voices every sound in the game is made of (doc 18 §1). Procedural
 * WebAudio: no files, nothing to download, nothing to license.
 *
 * `tone` is an oscillator that slides — a harpoon, a UI blip, the Core taking a
 * hit. `noise` is filtered white noise that sweeps its band — water, impacts,
 * anything that hisses or thumps. `bell` is two sines a non-integer ratio apart,
 * which is what makes a struck object sound struck rather than plucked.
 *
 * Every builder returns when the voice ENDS, in seconds, so the budget upstairs
 * knows how long it is holding a slot.
 */

export type Wave = 'sine' | 'triangle' | 'square' | 'sawtooth';
export type Band = 'lowpass' | 'highpass' | 'bandpass';

export interface ToneVoice {
  kind: 'tone';
  wave: Wave;
  from: number;
  to: number;
  dur: number;
  attack?: number;
  delay?: number;
  gain: number;
}

export interface NoiseVoice {
  kind: 'noise';
  dur: number;
  attack?: number;
  delay?: number;
  band: Band;
  from: number;
  to: number;
  q: number;
  gain: number;
}

export interface BellVoice {
  kind: 'bell';
  root: number;
  /** Non-integer on purpose: 2.01 rings, 2.00 hums. */
  ratio: number;
  dur: number;
  delay?: number;
  gain: number;
}

export type Voice = ToneVoice | NoiseVoice | BellVoice;

export interface SoundDef {
  bus: 'music' | 'sfx' | 'ambience';
  priority: 'critical' | 'feedback' | 'ambient';
  gain: number;
  voices: Voice[];
}

/** How long a sound occupies a slot: the last voice to finish decides. */
export function durationOf(def: SoundDef): number {
  return def.voices.reduce((m, v) => Math.max(m, (v.delay ?? 0) + v.dur), 0);
}

/** One second of white noise, made once and shared by every noise voice. */
export function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const data = buf.getChannelData(0);
  // a deterministic LCG rather than Math.random: the same hiss every session,
  // and one less reason for a bug report to be unreproducible
  let seed = 0x2f6e2b1;
  for (let i = 0; i < data.length; i++) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    data[i] = (seed / 0xffffffff) * 2 - 1;
  }
  return buf;
}

/**
 * Build one voice into `dest`, starting at `at` seconds on the context clock.
 * Returns the node whose `stop()` cuts it short, or null for nothing stoppable.
 */
export function playVoice(
  ctx: BaseAudioContext,
  dest: AudioNode,
  noise: AudioBuffer,
  v: Voice,
  at: number,
): AudioScheduledSourceNode | null {
  const t = at + (v.delay ?? 0);
  const gain = ctx.createGain();
  gain.connect(dest);

  if (v.kind === 'bell') {
    // two sines a non-integer ratio apart, each with its own decay: the partial
    // dies first, which is exactly what a struck metal thing does
    for (const [mult, share, decay] of [
      [1, 1, 1],
      [v.ratio, 0.45, 0.6],
    ] as const) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(v.root * mult, t);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(v.gain * share, t + 0.006);
      g.gain.exponentialRampToValueAtTime(0.0001, t + v.dur * decay);
      osc.connect(g).connect(gain);
      osc.start(t);
      osc.stop(t + v.dur + 0.02);
    }
    gain.gain.setValueAtTime(1, t);
    return null;
  }

  const attack = v.attack ?? 0.004;
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(v.gain, t + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + v.dur);

  if (v.kind === 'tone') {
    const osc = ctx.createOscillator();
    osc.type = v.wave;
    osc.frequency.setValueAtTime(v.from, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, v.to), t + v.dur);
    osc.connect(gain);
    osc.start(t);
    osc.stop(t + v.dur + 0.02);
    return osc;
  }

  const src = ctx.createBufferSource();
  src.buffer = noise;
  src.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = v.band;
  filter.Q.setValueAtTime(v.q, t);
  filter.frequency.setValueAtTime(v.from, t);
  filter.frequency.exponentialRampToValueAtTime(Math.max(20, v.to), t + v.dur);
  src.connect(filter).connect(gain);
  src.start(t);
  src.stop(t + v.dur + 0.02);
  return src;
}
